import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { roughieNote } from "@/components/analysis/RoughieDetail";

export const BotPublish = ({ m, r, onDone }) => {
  const api = useApi();
  const sugs = r.scan.suggestions;
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => sugs.map((s) => s.number));
  const [notes, setNotes] = useState(() => Object.fromEntries(sugs.map((s) => [s.number, s.role === "roughie" ? roughieNote(s) : s.reason || ""])));
  const [rec, setRec] = useState(r.scan.summary || "");
  const [busy, setBusy] = useState(false);

  if (r.status === "published")
    return <Link to={`/meeting/${m.id}?race=${r.number}`} data-testid={`bot-published-${m.id}-${r.number}`} className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300"><CheckCircle2 className="w-4 h-4" />Published on the site — view</Link>;
  if (!sugs.length) return null;
  if (!open)
    return <button data-testid={`bot-approve-${m.id}-${r.number}`} onClick={() => setOpen(true)} className="mt-3 px-4 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold">Approve & publish…</button>;

  const publish = async () => {
    const selections = sugs.filter((s) => picked.includes(s.number)).map((s) => ({ number: s.number, horse: s.horse, role: s.role, note: (notes[s.number] || "").slice(0, 500) }));
    if (!selections.length) { toast.error("Tick at least one selection"); return; }
    setBusy(true);
    try {
      await api("post", `/admin/meetings/${m.id}/races/${r.number}/publish-scan`, { selections, recommendation: rec.slice(0, 3000), status: "published" });
      toast.success(`${m.track} R${r.number} tips are live`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid={`bot-approve-form-${m.id}-${r.number}`}>
      <p className="text-xs text-slate-400">Tick the selections to publish and edit the comments. Top pick first.</p>
      {sugs.map((s) => (
        <div key={s.number} className="flex gap-2 items-start">
          <input type="checkbox" className="mt-2" data-testid={`bot-pick-${m.id}-${r.number}-${s.number}`} checked={picked.includes(s.number)}
            onChange={(e) => setPicked(e.target.checked ? [...picked, s.number] : picked.filter((x) => x !== s.number))} />
          <div className="flex-1">
            <p className="text-sm font-bold">{s.number}. {s.horse} <span className="text-[10px] uppercase text-slate-500">{s.role}</span>
              <span className="font-mono text-xs font-normal text-amber-300 ml-1" data-testid={`bot-pub-price-${m.id}-${r.number}-${s.number}`}>{s.odds ? `$${Number(s.odds).toFixed(2)}` : "no price yet"}</span></p>
            <input value={notes[s.number]} onChange={(e) => setNotes({ ...notes, [s.number]: e.target.value })} maxLength={500}
              className="w-full bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs" data-testid={`bot-note-${m.id}-${r.number}-${s.number}`} />
          </div>
        </div>
      ))}
      <textarea value={rec} onChange={(e) => setRec(e.target.value)} rows={2} maxLength={3000} placeholder="Public write-up"
        className="w-full bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs" data-testid={`bot-rec-${m.id}-${r.number}`} />
      <div className="flex gap-2">
        <button data-testid={`bot-publish-${m.id}-${r.number}`} onClick={publish} disabled={busy} className="px-4 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold disabled:opacity-50">{busy ? "Publishing…" : "Publish to site"}</button>
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-slate-400 hover:text-white">Cancel</button>
      </div>
    </div>
  );
};
